import Parse from "parse"
import moment from "moment"
import dayjs from "dayjs"
import { parseLimitRequest, roundNumber } from "../../utils"
import { getProductTypeLabel, newAmbientProducts } from "../../utils/dispatchUtils"
import { STOCK_TYPES } from "../../utils/stockTypes"

const DistributionCenters = Parse.Object.extend("DistributionCenters")
const Recipe = Parse.Object.extend("Recipe")
const SubcontractorsProducts = Parse.Object.extend("SubcontractorsProducts")

export const getProductStockById = async function (idProductStock) {
	try {
		const productStock = await new Parse.Query("ProductsStock")
			.include("events.user")
			.equalTo("objectId", idProductStock)
			.first()

		return productStock
	} catch (e) {
		throw e
	}
}

export const getReceptionProductsStock = async function (date, brand, idHub) {
  try {
    const productStocks = []
    const productsStockResults = await new Parse.Query("ProductsStock")
      .include("events.user")
      .include("uniqueCode")
      .equalTo("saleDate", moment.utc(parseFloat(date)).startOf("day").valueOf())
      .equalTo("brand", brand)
      .greaterThanOrEqualTo("initialVolume", 0)
      .equalTo("hub", DistributionCenters.createWithoutData(idHub))
      // .ascending("uniqueCode")
      .limit(parseLimitRequest)
      .find()

    for (const productsStock of productsStockResults) {
      const productDispatch = productsStock.get("productDispatch")
      const distributionCenter = productsStock.get("hub")
      const productionItem = await new Parse.Query("ProductionItem")
        .equalTo("productDispatch", productDispatch)
        .limit(parseLimitRequest)
        .first()

      if (
        (distributionCenter.has("packagingTypes")
          && productionItem
          && distributionCenter.get("packagingTypes").includes(productionItem.get("packaging")))
        || newAmbientProducts.includes(productsStock.get("productType"))
      ) {
        productStocks.push(productsStock)
      }
    }

    return productStocks ? productStocks.map(productStock => productStock.toJSON()) : []
  } catch (e) {
    console.error(e)
    throw e
  }
}

export const downloadProductsStocksInventory = async function (productTypeOptions=[]) {
	try {
		const currentDate = dayjs.utc().startOf("day").valueOf()

		const rows = []
		await new Parse.Query("ProductsStock")
			.select(["events", "productType", "uniqueCode", "commercialTitle", "dlc", "volume", "hub.name", "productDispatch.idProduct", "isReusable"])
			.include(["hub", "productDispatch"])
			.lessThanOrEqualTo("saleDate", currentDate)
			.greaterThanOrEqualTo("dlc", currentDate)
			.greaterThan("volume", 0)
			.equalTo("receptionStatus", "DONE")
			.each(async productsStock => {
				const distributionCenter = productsStock.get("hub")
				const productDispatch = productsStock.get("productDispatch")

				const inventoryEvents = productsStock.get("events").filter(event => event.type === "INVENTORY")
				const lastInventoryEvent = inventoryEvents[inventoryEvents.length - 1]

				const row = {
					hub: distributionCenter.get("name"),
					productType: getProductTypeLabel(productsStock.get("productType"), productTypeOptions),
					uniqueCode: productsStock.get("uniqueCode"),
					commercialTitle: productsStock.get("commercialTitle"),
					isReusable: productsStock.get("isReusable") ? "Oui" : "Non",
					dlc: dayjs.utc(productsStock.get("dlc")).format("DD/MM/YYYY"),
					volume: productsStock.get("volume"),
					lastInventoryEventDate: lastInventoryEvent ? dayjs(lastInventoryEvent.date).format("DD/MM/YYYY HH:mm:ss") : ""
				}

				const recipe = await new Parse.Query(Recipe).select("foodcost").equalTo("objectId", productDispatch.get("idProduct")).first()
				let foodCost = null

				if (recipe) {
					foodCost = recipe.get("foodcost")
				} else {
					const subcontractorsProduct = await new Parse.Query(SubcontractorsProducts).select("totalCost").equalTo("objectId", productDispatch.get("idProduct")).first()
					if (subcontractorsProduct) {
						foodCost = subcontractorsProduct.get("totalCost")
					}
				}

				row.foodCost = foodCost ? roundNumber(foodCost, 3) : null
				rows.push(row)
			})

		return rows
	} catch (e) {
		throw e
	}
}

export const getInventoryProductsStock = async (brand, idHub) => {
	try {
		const date = moment.utc().startOf("day").valueOf()

		// request on sale date
		const saleDateQuery = new Parse.Query("ProductsStock")
			.equalTo("saleDate", date)

		// request on dlc
		const dlcQuery = new Parse.Query("ProductsStock")
			.greaterThanOrEqualTo("dlc", date)

		// distributionCenter pointer
		const distributionCenter = new DistributionCenters()
		distributionCenter.id = idHub

		const productsStocks = await Parse.Query.or(saleDateQuery, dlcQuery)
			.include("uniqueCode")
			.greaterThan("initialVolume", 0)
			.equalTo("receptionStatus", "DONE")
			.equalTo("brand", brand)
			.equalTo("hub", distributionCenter)
			.include("events.user")
			.ascending("uniqueCode")
			.limit(parseLimitRequest)
			.find()

		return productsStocks ? productsStocks.map(productStock => productStock.toJSON()) : []
	} catch (e) {
		throw e
	}
}

/**
 * Books a breakage against one fridge.
 *
 * Separate from `updateProductStockVolume` on purpose: that one always writes the click & collect
 * stock, which is right for a reception or an inventory and wrong here. Reusing it with one more
 * flag would have put the breakage on the same code path as the two writes it must not disturb.
 *
 * `volume` stays the legacy alias of the click & collect stock, so the two are written together
 * and can never drift apart.
 */
export const applyFlawToStock = async function (idProductStock, stockType, remainingVolume, events) {
	const productStock = await getProductStockById(idProductStock)

	if (!productStock) return Promise.reject(`productStock : ${idProductStock} not exist`)

	const remaining = Math.max(0, parseInt(remainingVolume))

	if (STOCK_TYPES.LIBRE_SERVICE === stockType) {
		productStock.set("volumeLibreService", remaining)
	} else {
		productStock.set("volume", remaining)
		productStock.set("volumeClickAndCollect", remaining)
	}

	// A stock written by an import or a migration may carry no events at all.
	productStock.set("events", [...(productStock.get("events") || []), ...events])

	await productStock.save()

	return productStock.toJSON()
}

export const updateProductStockVolume = async function (idProductStock, volume, data, type, volumeLibreService = null) {
	try {
		const productStock = await getProductStockById(idProductStock)

		if (productStock) {
			productStock.set("volume", parseInt(volume))
			// KFC-191 approach A: `volume` stays the legacy alias of the click & collect stock,
			// which is what the FoodCheri pubSub reads. The two are written together so they can
			// never drift apart.
			productStock.set("volumeClickAndCollect", parseInt(volume))

			if (type === "RECEPTION") {
				productStock.set("receptionStatus", "DONE")
				if (volumeLibreService !== null) {
					productStock.set("volumeLibreService", parseInt(volumeLibreService))
				}
			}
			if (type === "INVENTORY") productStock.set("inventoryStatus", "DONE")
			productStock.set("events", [...productStock.get("events"), ...data])

			await productStock.save()
			return productStock.toJSON()
		} else {
			return Promise.reject(`productStock : ${idProductStock} not exist`)
		}
	} catch (e) {
		throw e
	}
}

/**
 * KFC-285: whether a fridge was actually counted.
 *
 * Zero is a count — a shelf can legitimately be empty — so only an absent value means "this
 * fridge was not recounted".
 */
const isCounted = (value) => value !== undefined && value !== null && "" !== value

/**
 * KFC-285: an inventory taken fridge by fridge.
 *
 * Deliberately kept apart from `updateProductStockVolume`, which reception, breakage and the
 * single-fridge inventory all go through: that function always writes the click & collect
 * stock, so reusing it for a self-service count would silently zero the other fridge. The few
 * duplicated lines buy the certainty of not moving behaviour that KFC-284 has already recetted.
 *
 * `counts` carries only the fridges that were counted; a fridge left out keeps its stock.
 */
export const updateProductStockInventory = async function (idProductStock, counts, events, allDeclaredCounted) {
	try {
		const productStock = await getProductStockById(idProductStock)

		if (!productStock) return Promise.reject(`productStock : ${idProductStock} not exist`)

		// A save that fails leaves its pending operations queued on the Parse object, and the SDK
		// hands back that very instance on the next query — single instance is on by default in
		// the browser. `get("events")` then returns the estimated value, failed attempt included,
		// so the retry behind the "Réessayer" button would append its event on top and persist the
		// same count twice. Dropping the unsaved local state puts us back on the server's version.
		productStock.revert()

		if (isCounted(counts.clickAndCollect)) {
			// `volume` stays the legacy alias of the click & collect stock, written together with
			// its new name so that the two can never drift apart.
			productStock.set("volume", parseInt(counts.clickAndCollect))
			productStock.set("volumeClickAndCollect", parseInt(counts.clickAndCollect))
		}

		if (isCounted(counts.libreService)) {
			productStock.set("volumeLibreService", parseInt(counts.libreService))
		}

		// A product counted on one fridge only is not inventoried yet: it stays in "à
		// inventorier", which is exactly what the toolbar filter is there to show.
		productStock.set("inventoryStatus", allDeclaredCounted ? "DONE" : "TODO")
		// `|| []` because a stock with no history at all is a real case, and spreading undefined
		// would throw a TypeError that the panel would then present as a plain "réessayez".
		productStock.set("events", [...(productStock.get("events") || []), ...events])

		await productStock.save()
		return productStock.toJSON()
	}
	catch (e) {
		throw e
	}
}

/**
 * KFC-351: un transfert entre les deux frigos d'un meme produit.
 *
 * Les deux volumes sont ecrits ensemble, dans une seule sauvegarde : un transfert deplace du
 * stock, il n'en cree ni n'en detruit, et les laisser diverger le temps de deux ecritures
 * ouvrirait une fenetre ou le total serait faux.
 *
 * `inventoryStatus` revient a "TODO" dans la meme sauvegarde. Ouvrir le panneau l'avait pose a
 * "IN_PROGRESS" et pris un verrou ; le transfert n'est pas un comptage, donc le produit reste a
 * inventorier. L'ecrire ici plutot que dans un second appel evite de laisser le produit bloque
 * en "IN_PROGRESS" si l'ecriture qui suivrait echouait.
 */
export const updateProductStockTransfer = async function (idProductStock, volumes, event) {
	try {
		const productStock = await getProductStockById(idProductStock)

		if (!productStock) return Promise.reject(`productStock : ${idProductStock} not exist`)

		// Meme raison que pour l'inventaire : une sauvegarde en echec laisse ses operations en
		// attente sur l'objet, et le SDK rend cette meme instance a la requete suivante. Sans
		// cela, un reessai relirait l'evenement de la tentative echouee et l'ecrirait deux fois.
		productStock.revert()

		// `volume` reste l'alias historique du click & collect, ecrit avec son nouveau nom pour
		// que les deux ne puissent pas diverger.
		productStock.set("volume", parseInt(volumes.clickAndCollect))
		productStock.set("volumeClickAndCollect", parseInt(volumes.clickAndCollect))
		productStock.set("volumeLibreService", parseInt(volumes.libreService))
		productStock.set("inventoryStatus", "TODO")
		productStock.set("events", [...(productStock.get("events") || []), event])

		await productStock.save()
		return productStock.toJSON()
	}
	catch (e) {
		throw e
	}
}

export const updateProductStockStatus = async function (productStock, reset = false) {
	try {
		if (reset) {
			productStock.set("receptionStatus", "TODO")
		} else {
			productStock.set("receptionStatus", "IN_PROGRESS")
		}

		await productStock.save()
		return productStock.toJSON()
	}
	catch (e) {
		throw e
	}
}

export const updateProductStockInventoryStatus = async function (productStock, reset = false) {
	try {
		if (reset) {
			productStock.set("inventoryStatus", "TODO")
		} else {
			productStock.set("inventoryStatus", "IN_PROGRESS")
		}

		await productStock.save()
		return productStock.toJSON()
	}
	catch (e) {
		throw e
	}
}

export const getProductStockWithEventsByDate = async function (startDate, endDate, brand) {
	try {
		const productsStock = []

		await new Parse.Query("ProductsStock")
			.include("hub")
			.include("events.user")
			.include("events.type")
			.include("events.date")
			.containedIn("events.type", ["RECEPTION", "FLAW"])
			.greaterThanOrEqualTo("events.date", startDate)
			.lessThanOrEqualTo("events.date", endDate)
			.equalTo("brand", brand)
			.each(
				productStock => productsStock.push(productStock),
				{
					batchSize: 500
				}
			)

		return productsStock.length ? productsStock.map(el => el.toJSON()) : []
	} catch (e) {
		throw e
	}
}

export const setEventChecked = async function (event, comment) {
	try {
		const productStock = await getProductStockById(event.idProductStock)

		if (productStock) {
			// checked log
			const checked = {
				user: Parse.User.current(),
				date: moment.utc().valueOf(),
				comment
			}

			const events = productStock.get("events")

			// update events
			event.indexes.forEach(i => {
				events[i].checked = checked
			})

			productStock.set("events", events)
			await productStock.save()
			return checked
		} else {
			return Promise.reject(`productStock : ${event.idProductStock} not exist`)
		}
	} catch (e) {
		throw e
	}
}

export const updateInitialVolumeProductStocks = async function (productStocks) {
	try {
		const listProducts = []
		await new Parse.Query("ProductsStock")
			.containedIn("objectId", productStocks)
			.each(async productStock => {
				productStock.set("initialVolume", 1)
				await productStock.save()
				listProducts.push(productStock.toJSON())
			})

		return listProducts
	} catch (e) {
		throw e
	}
}

export async function findProductStock(hub, dlc, subcontractorProductId) {
	const item = await new Parse.Query("ProductsStock")
		.include(["productDispatch"])
		.equalTo("hub", hub)
		.equalTo("dlc", dlc)
		.equalTo("receptionStatus", "DONE")
		.first()

	if (item) {
		const jsonItem = item.toJSON()
		if (jsonItem.productDispatch && jsonItem.productDispatch.idProduct === subcontractorProductId) {
			return true
		}
	}

	return false
}

export async function getProductsStockByStatus(hubId, dlc, subcontractorProductId, status) {
	const hubPointer = DistributionCenters.createWithoutData(hubId)
	const productStocks = []
	await new Parse.Query("ProductsStock")
		.include(["productDispatch"])
		.equalTo("hub", hubPointer)
		.equalTo("dlc", dlc)
		.equalTo("receptionStatus", status)
		.each(productStock => {
			if (productStock.get("productDispatch") && productStock.get("productDispatch").get("idProduct") === subcontractorProductId) {
				productStocks.push(productStock)
			}
		})

	return productStocks
}

